import Card from "components/common/Card";
import { useEffect, useState } from "react";
import BenefitsTabs from "./components/benefitsTabs";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import { useParams } from "react-router-dom";
import { MobileBenefitCardSkeleton, WebBenefitCardSkeleton } from "./components/Skeleton/BenefitCard";
import { getProductsDataByProductId } from "store/thunk/recommendation.thunk";
import RecommendationbenefitCard from "./components/recommendationbenefitCard";
import Button from "components/semantic/Button";
import { areAllObjectsKeysNull } from "utils/utils";
import { baseUrl } from "config/defaultUrl";
import { toast } from "react-toastify";

// Success
const successStyle = {
  background: "#f0fff4",         
  borderLeft: "5px solid #28a745",
  borderRadius: "8px",
  color: "#155724",
  boxShadow: "0 4px 12px rgba(40,167,69,0.15)"
};

// Error
const errorStyle = {
  background: "#fff5f5",          
  borderLeft: "5px solid #dc3545",
  borderRadius: "8px",
  color: "#721c24",
  boxShadow: "0 4px 12px rgba(220,53,69,0.15)"
};

const RecommendationBenefitSummaryV1 = () => {
  const { productId } = useParams<{ productId: string }>();
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState("coverageAndLimits");
  const [isDownloading, setIsDownloading] = useState(false);

  const benefitSummaryList = useAppSelector((state) => state.rootReducer.recommendationSlice.benefitSummary);
  const benefitSummaryLoading = useAppSelector((state) => state.rootReducer.recommendationSlice.benefitSummaryLoading);

  const fetchDownloadUrl = async (productId: any) => {
    const res = await fetch(`${baseUrl}insa-recommendation/products/${productId}/generate-pdf`);

    if (!res.ok) {
      throw new Error("Failed to fetch download URL");
    }

    const data = await res.json();

    return data?.data;
  };

  const downloadFromUrl = async (url: string) => {
    const res = await fetch(url, { cache: "no-store" });
    const blob = await res.blob();

    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `product_summary.pdf`;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownload = async (productId: any) => {
    if (isDownloading) return;
    setIsDownloading(true)
    try {
      const { pdfUrl } = await fetchDownloadUrl(productId);
      await downloadFromUrl(pdfUrl);
  
      toast.success("Downloaded successfully", {
        autoClose: 1200,
        style: successStyle
      });
    } catch (err) {
      toast.error("Download failed", {
        autoClose: 1500,
        style: errorStyle
      });
      console.error(err);
    } finally {
      setIsDownloading(false)
    }
  };

  useEffect(() => {
    if (productId) {
      dispatch(getProductsDataByProductId({ productId }));
    }
  }, [productId]);

  const isEmptyObject = (obj?: Record<string, any>) => !obj || Object.keys(obj).length === 0;

  const hasData = benefitSummaryList && !isEmptyObject(benefitSummaryList);

  const handleNavigate = async (id: any) => {
    const hasOtherBenefits = !areAllObjectsKeysNull(benefitSummaryList?.otherBenefitsAndLimits);
    const hasWaitingPeriod = !areAllObjectsKeysNull(benefitSummaryList?.waitingPeriodAndSubLimitsV2);
    const hasExclusions = Array.isArray(benefitSummaryList?.exclusions) && benefitSummaryList.exclusions.length > 0;

    if (id) {
      setActiveTab(id);
      return;
    }

    switch (activeTab) {
      case "coverageAndLimits":
        if (hasOtherBenefits) setActiveTab("otherBenefitsAndLimits");
        else if (hasWaitingPeriod) setActiveTab("waitingPeriodAndSubLimitsV2");
        else setActiveTab("exclusions");
        break;

      case "otherBenefitsAndLimits":
        if (hasWaitingPeriod) setActiveTab("waitingPeriodAndSubLimitsV2");
        else setActiveTab("exclusions");
        break;

      case "waitingPeriodAndSubLimitsV2": {
        if (hasExclusions) {
          setActiveTab("exclusions");
        } else {
          await handleDownload(productId);
        }
        break;
      }

      case "exclusions": {
        await handleDownload(productId);
        break;
      }

      default:
        break;
    }
  };

  const renderBenefitCard = () => {
    if (benefitSummaryLoading) {
      return (
        <>
          <WebBenefitCardSkeleton />
          <MobileBenefitCardSkeleton />
        </>
      );
    }

    if (!hasData) return null;

    const tabMap: Record<string, any> = {
      coverageAndLimits: benefitSummaryList?.coverageAndLimits,
      otherBenefitsAndLimits: benefitSummaryList?.otherBenefitsAndLimits,
      waitingPeriodAndSubLimitsV2: benefitSummaryList?.waitingPeriodAndSubLimitsV2,
      exclusions: benefitSummaryList?.exclusions
    };

    const cardData = tabMap[activeTab];

    return cardData ? <RecommendationbenefitCard id={activeTab} cardData={cardData} /> : null;
  };

  return (
    <div>
      <h1 className="text-2xl font-extrabold my-3 md:mb-4 text-center">Benefits Summary</h1>

      <Card className="w-full" parentClassName="rounded-md md:rounded-lg border-2 border-gray-300 shadow-none">
        <BenefitsTabs activeTab={activeTab} setActiveTab={setActiveTab} version={benefitSummaryList?.version} benefitSumList={benefitSummaryList} />
      </Card>

      {renderBenefitCard()}

      {hasData && (
        <>
          <Card className="w-full hidden md:flex justify-center py-5" parentClassName="border-2 border-gray-300 shadow-none">
            <Button title={activeTab === "exclusions" ? "Download PDF" : "Next"} onClick={() => handleNavigate(null)} isLoading={isDownloading}/>
          </Card>

          <Card className="w-full fixed bottom-0 left-0 rounded-t-[15px] bg-white flex md:hidden justify-center py-5" parentClassName="border-2 border-gray-300 shadow-none">
            <Button title={activeTab === "exclusions" ? "Download PDF" : "Next"} onClick={() => handleNavigate(null)} isLoading={isDownloading}/>
          </Card>
        </>
      )}
    </div>
  );
};

export default RecommendationBenefitSummaryV1;
